import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useAuth } from '../../Common/Configurations/AuthContext';
import "flatpickr/dist/themes/material_blue.css";
import { Link, useLocation, useNavigate } from 'react-router-dom';
import config from '../../Common/Configurations/APIConfig';
import apiService from '../../Common/Configurations/APIService';
import DefaultData from '../../CompanyData/DefaultData';
import { FaWhatsapp, FaEnvelope, FaInfoCircle } from "react-icons/fa";

const HOTELS_PER_PAGE = 20;
const DEFAULT_HOTEL_IMAGE = "/assets/images/Default_Hotel.jpg";

const HotelsList = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { state } = useLocation();
  const [searchQuery, setSearchQuery] = useState({});
  const [allHotels, setAllHotels] = useState([]);
  const [pageLoading, setPageLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState("");
  const [currency, setCurrency] = useState(DefaultData?.DEFAULTCURRENCY || "INR");
  const [exchangeRates, setExchangeRates] = useState([]);
  const [conversionFactor, setConversionFactor] = useState(1);
  const [showMarkup, setShowMarkup] = useState(true);

  const [filters, setFilters] = useState({
    propertyType: [],
    priceRange: [0, 100000],
    rating: [],
    cityName: []
  });
  const [displayedHotels, setDisplayedHotels] = useState([]);
  const [hasMore, setHasMore] = useState(true);
  const observer = useRef();


   const profileRate = useMemo(() => {
    return exchangeRates.find(r => r.toCurrency === user?.currency)?.price || 1;
  }, [exchangeRates, user]);

  // 2. Helper to get markup value for a specific hotel IN INR
  const getMarkupInINR = useCallback((hotel) => {
    if (!user || !hotel) return 0;
    const countryCode = hotel.countryCode?.toUpperCase();
    const markupInUserCurrency = countryCode === "IN"
      ? (parseFloat(user.domesticHotelMarkup) || 0)
      : (parseFloat(user.internationalHotelMarkup) || 0);
    
    return markupInUserCurrency / profileRate;
  }, [user, profileRate]);

  const sidebarMarkupINR = useMemo(() => {
    if (allHotels.length > 0) {
      return getMarkupInINR(allHotels[0]);
    }
    return 0;
  }, [allHotels, getMarkupInINR]);



  useEffect(() => {
    const getCurrencies = async () => {
      try {
        const response = await apiService.get(`${config.GETCURRENCY}`, 1000, 1);
        if (response && response.data) {
          setExchangeRates(response.data);
        }
      } catch (error) {
        console.error("Error fetching currencies", error);
      }
    };
    getCurrencies();
  }, []);

   useEffect(() => {
    if (user?.currency && exchangeRates.length > 0) {
      const userCurrency = user.currency;
      setCurrency(userCurrency);
      if (userCurrency === "INR") {
        setConversionFactor(1);
      } else {
        const rateData = exchangeRates.find(r => r.fromCurrency === "INR" && r.toCurrency === userCurrency);
        setConversionFactor(rateData ? rateData.price : 1);
      }
    }
  }, [user, exchangeRates]);

  const handleCurrencyChange = (e) => {
    const selectedCode = e.target.value;
    setCurrency(selectedCode);
    if (selectedCode === "INR") {
      setConversionFactor(1);
    } else {
      const rateData = exchangeRates.find(r => r.fromCurrency === "INR" && r.toCurrency === selectedCode);
      setConversionFactor(rateData ? rateData.price : 1);
    }
  };
  // --- 3. Price Conversion Helper ---
  const calculatePrice = (priceInINR, hotel = null) => {
    if (!priceInINR) return 0;

    const markupInINRValue = showMarkup ? getMarkupInINR(hotel) : 0;
    const totalInINR = parseFloat(priceInINR) + markupInINRValue;

    const finalPrice = totalInINR * conversionFactor;

    if (currency === "INR") {
      return Math.round(finalPrice).toLocaleString("en-IN");
    } else {
      return finalPrice.toFixed(2);
    }
  };

  // Memoized filtered hotels based on filters
  const filteredHotels = useMemo(() => {
    if (!allHotels || allHotels.length === 0) {
      return [];
    }
    return allHotels.filter(hotel => {
      const propertyType = hotel?.propertyType;
      const cityName = hotel?.cityName;
      const rating = Math.floor(hotel?.rating || 0);
      const markupVal = showMarkup ? getMarkupInINR(hotel) : 0;
      const activePriceConverted = (parseFloat(hotel?.totalSalePrice || 0) + markupVal) * conversionFactor;
      return (
        (filters.propertyType.length === 0 || filters.propertyType.includes(propertyType)) &&
        (filters.cityName.length === 0 || filters.cityName.includes(cityName)) &&
        (filters.rating.length === 0 || filters.rating.includes(rating)) &&
         (activePriceConverted >= filters.priceRange[0] && activePriceConverted <= filters.priceRange[1])
      );
    });
  }, [allHotels, filters]);

  // Memoized filter options
  const { propertyTypes, locations, ratingCounts } = useMemo(() => {
    if (!allHotels || allHotels.length === 0) {
      return { propertyTypes: [], locations: [], ratingCounts: [] };
    }
    const propTypeCounts = {};
    const cityCounts = {};
    const ratingCounts = {};

    allHotels.forEach(hotel => {
      const type = hotel.propertyType || 'Unknown';
      propTypeCounts[type] = (propTypeCounts[type] || 0) + 1;

      const city = hotel.cityName || 'Unknown';
      cityCounts[city] = (cityCounts[city] || 0) + 1;

      const rating = Math.floor(hotel.rating || 0);
      ratingCounts[rating] = (ratingCounts[rating] || 0) + 1;
    });

    const propertyTypes = Object.entries(propTypeCounts).map(([type, count]) => ({ type, count }));
    const locations = Object.entries(cityCounts).map(([type, count]) => ({ type, count }));
    const ratings = Object.entries(ratingCounts)
      .sort((a, b) => b[0] - a[0])
      .map(([rating, count]) => ({ rating: parseInt(rating), count }));

    return { propertyTypes, locations, ratingCounts: ratings };
  }, [allHotels]);


  // Effect to handle filtering and resetting displayed hotels
  useEffect(() => {
    setDisplayedHotels(filteredHotels.slice(0, HOTELS_PER_PAGE));
    setHasMore(filteredHotels.length > HOTELS_PER_PAGE);
  }, [filteredHotels]);

  // Function to load more hotels
  const loadMoreHotels = useCallback(() => {
    if (loadingMore || !hasMore) return;

    setLoadingMore(true);
    const currentCount = displayedHotels.length;
    const nextHotels = filteredHotels.slice(currentCount, currentCount + HOTELS_PER_PAGE);

    setDisplayedHotels(prev => [...prev, ...nextHotels]);

    if (currentCount + nextHotels.length >= filteredHotels.length) {
      setHasMore(false);
    }
    setLoadingMore(false);
  }, [loadingMore, hasMore, filteredHotels, displayedHotels.length]);

  // Use a useCallback to create the ref for the last element
  const lastHotelElementRef = useCallback(node => {
    if (pageLoading || loadingMore) return;
    if (observer.current) observer.current.disconnect();

    observer.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasMore) {
        loadMoreHotels();
      }
    }, { rootMargin: '200px' });

    if (node) observer.current.observe(node);
  }, [pageLoading, loadingMore, hasMore, loadMoreHotels]);

  const handleFilterChange = useCallback((type, value) => {
    setFilters(prev => {
      if (['propertyType', 'rating', 'cityName'].includes(type)) {
        const updated = prev[type].includes(value)
          ? prev[type].filter(v => v !== value)
          : [...prev[type], value];
        return { ...prev, [type]: updated };
      } else if (type === 'priceRange') {
        return { ...prev, priceRange: value };
      }
      return prev;
    });
  }, []);

  const handleClearFilters = useCallback(() => {
    setFilters({
      propertyType: [],
      priceRange: [0, 100000],
      rating: [],
      cityName: []
    });
  }, []);

  const getHotels = useCallback(async (searchQuery) => {
    setPageLoading(true);
    setError("");
    try {
      const result = await apiService.post(config.SEARCHHOTEL, searchQuery);
      if (result.succeeded) {
        if (result.data?.commonHotelDetails.length === 0) {
          setError("No Hotels Found. Please try for other dates");
          setAllHotels([]);
        } else {
          setAllHotels(result.data.commonHotelDetails);
        }
      } else {
        setError(result?.message || "An unknown error occurred.");
        setAllHotels([]);
      }
    } catch (err) {
      setError("Failed to fetch hotels.");
      setAllHotels([]);
    } finally {
      setPageLoading(false);
    }
  }, []);

  const handleHotel = (e, hotel) => {
    e.preventDefault();
    navigate('/hotel/details/', { state: { searchQuery, hotel,  markupInINR: showMarkup ? getMarkupInINR(hotel) : 0  } });
  }

  // Initial data fetch
  useEffect(() => {
    if (!state || !state.searchQuery) {
      navigate("/hotel/search");
    } else {
      setSearchQuery(state.searchQuery);
      getHotels(state.searchQuery);
    }
  }, [state, navigate, getHotels]);

  const renderHotelCards = () => {
    if (displayedHotels.length === 0 && !loadingMore) {
      if (pageLoading) return null; // Wait for initial load
      if (allHotels.length > 0 && filteredHotels.length === 0) {
        return <p className='text-center'>No hotels match your filters.</p>;
      }
      return <p className='text-center'>{error || "No hotels found."}</p>;
    }

    return displayedHotels.map((hotel, index) => {
      const isLastElement = displayedHotels.length === index + 1;
      return (
        <div
          key={hotel.hotelId}
          className="hotels-item bg-white shadow-md rounded p-3 mb-3"
          ref={isLastElement ? lastHotelElementRef : null}
        >
          <div className="row">
            <div className="col-md-4">
              <img className="img-fluid rounded align-top" style={{ minWidth: "280px", maxWidth: "280px", minHeight: "200px", maxHeight: "200px" }} src={hotel.mainImage || DEFAULT_HOTEL_IMAGE} loading="lazy" alt={hotel.hotelName} /></div>
            <div className="col-md-8 ps-3 ps-md-0 mt-3 mt-md-0">
              <div className="row g-0">
                <div className="col-sm-9">
                  <p className="hotels-amenities d-flex align-items-center mb-2 text-4">
                    <span className="badge bg-success text-nowrap px-2">{hotel.propertyType}</span>
                  </p>
                  <h4 className="text-dark text-5">{hotel.hotelName}</h4>
                  <p className="mb-2">
                    <span className="me-2">
                      {Array.from({ length: 5 }, (_, i) => (
                        <span className='text-warning fs-5' key={i}>{i < hotel.rating ? '★' : '☆'}</span>
                      ))}
                    </span>
                    <span className="text-black-50">
                      <i className="ri-map-pin-line" /> {hotel.address}, {hotel.cityName}, {hotel.countryName}, {hotel.postalCode}</span>
                  </p>
                  <p className="hotels-amenities d-flex align-items-center mb-2 text-4">
                    <a
                      href={`http://googleusercontent.com/maps.google.com/3{hotel?.geoLocation?.latitude},${hotel?.geoLocation?.longitude}`}
                      target="_blank"
                      rel="noopener noreferrer">
                      View on Maps
                    </a>
                  </p>
                  <p className="text-success mb-0">CheckIn : {hotel.checkInTime} - CheckOut : {hotel.checkOutTime}</p>
                </div>
                <div className="col-sm-3 text-end d-flex d-sm-block align-items-center">
                  <div className="text-success text-3 mb-0 mb-sm-1 order-2 ">16% Off!</div>

                  {/* --- UPDATED PRICE DISPLAY --- */}
                  <div className="text-dark fs-4 fw-bold mb-2">
                    {currency} {calculatePrice(hotel.totalSalePrice, hotel)}
                  </div>
                  {/* ----------------------------- */}

                  <div className="text-black-50 mb-0 mb-sm-2 order-3 d-none d-sm-block">{hotel.commonHotelDefaultRoom?.mealType}</div>
                  <button onClick={(e) => handleHotel(e, hotel)} className="btn btn-sm btn-primary order-4 ms-auto">VIEW ROOMS</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    });
  };

  return (
    <div className="container-fluid mt-2">
      <div className="card pt-0">
        <div className="card-body">
          <div className="row">
            <div className="col-3">
              <div className="card">
                <div className="card-header">
                  <div className="d-flex mb-0">
                    <div className="flex-grow-1">
                      <h5 className="fs-16">Filters</h5>
                    </div>
                    <div className="flex-shrink-0">
                      <Link onClick={handleClearFilters} className="text-decoration-underline">Clear All</Link>
                    </div>
                  </div>
                </div>
                <div className='card-body'>
                  {/* --- CURRENCY DROPDOWN ADDED HERE --- */}
                  <div style={{ marginBottom: '20px' }}>
                    <p className="text-muted text-uppercase fs-12 fw-medium mb-2">Currency</p>
                    <select
                      className="form-select form-select-sm"
                      value={currency}
                      onChange={handleCurrencyChange}
                    >
                      <option value="INR">INR</option>
                      {exchangeRates.map((rate, index) => (
                        // CHANGE: Use rate.toCurrency here, because that is the currency you want to convert TO
                        <option key={index} value={rate.toCurrency}>
                          {rate.toCurrency}
                        </option>
                      ))}
                    </select>
                  </div>
                  {/* ------------------------------------ */}
                  {/* --- UPDATED MARKUP TOGGLE TO MATCH FLIGHTS --- */}
                   <div className={`p-3 border rounded mb-4 shadow-sm transition-all ${showMarkup ? 'bg-primary text-white' : 'bg-white text-dark'}`} 
                       style={{ transition: '0.3s ease' }}>
                    <div className="form-check form-switch mb-0">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="hotelMarkupToggle"
                        checked={showMarkup}
                        onChange={(e) => setShowMarkup(e.target.checked)}
                        style={{ cursor: 'pointer', borderColor: showMarkup ? '#fff' : '#ccc' }}
                      />
                      <label className={`form-check-label fw-bold ${showMarkup ? 'text-white' : 'text-dark'}`} htmlFor="hotelMarkupToggle" style={{ cursor: 'pointer' }}>
                        {showMarkup ? "+ Net" : "Net"}
                      </label>
                    </div>
                    <div className={`mt-2 d-flex align-items-center ${showMarkup ? 'text-white-50' : 'text-success'}`}>
      
                    </div>
                  </div>
{/* ---------------------------------------------- */}

                  <div style={{ marginTop: '10px' }}>
                    <p className="text-muted text-uppercase fs-12 fw-medium mb-2">Property Types</p>
                    {propertyTypes.map((item, idx) => (
                      <div key={idx}>
                        <label
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: '8px'
                          }}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <input
                              type="checkbox"
                              checked={filters.propertyType.includes(item.type)}
                              onChange={() => handleFilterChange('propertyType', item.type)}
                            />
                            <span>{item.type}</span>
                          </div>
                          <span className="text-muted">{item.count}</span>
                        </label>
                      </div>
                    ))}
                  </div>
                  <div style={{ marginTop: '10px' }}>
                    <p className="text-muted text-uppercase fs-12 fw-medium mb-2">Locations</p>
                    {locations.map((item, idx) => (
                      <div key={idx}>
                        <label
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: '8px'
                          }}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <input
                              type="checkbox"
                              checked={filters.cityName.includes(item.type)}
                              onChange={() => handleFilterChange('cityName', item.type)}
                            />
                            <span>{item.type}</span>
                          </div>
                          <span className="text-muted">{item.count}</span>
                        </label>
                      </div>
                    ))}
                  </div>
                  <div style={{ marginTop: '10px' }}>
                    <p className="text-muted text-uppercase fs-12 fw-medium mb-2">Star Ratings</p>
                    {ratingCounts.map((item, idx) => (
                      <div key={idx}>
                        <label
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: '8px'
                          }}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <input
                              type="checkbox"
                              checked={filters.rating.includes(item.rating)}
                              onChange={() => handleFilterChange('rating', item.rating)}
                            />
                            <span>
                              {Array.from({ length: 5 }, (_, i) => (
                                <span key={i} className="text-warning">
                                  {i < item.rating ? '★' : '☆'}
                                </span>
                              ))}
                            </span>
                          </div>
                          <span className="text-muted">{item.count}</span>
                        </label>
                      </div>
                    ))}
                  </div>
                  <div style={{ marginTop: '10px' }}>
                    <p className="text-muted text-uppercase fs-12 fw-medium mb-2">Price Range</p>
                    <input
                      type="range"
                      min={0}
                      max={100000}
                      style={{ width: "100%" }}
                      value={filters.priceRange[1]}
                      onChange={(e) =>
                        handleFilterChange('priceRange', [0, parseInt(e.target.value)])
                      }
                    />
                    <div>Up to {currency} {calculatePrice(filters.priceRange[1])}</div>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-lg-9 mt-4 mt-lg-0">
              {pageLoading === false && (
                <div className="border-bottom mb-3 pb-3">
                  <div className="row align-items-center">
                    <div className="col-6 col-md-8">
                      <span className="me-3">
                        <span className="text-4">{searchQuery?.displayFrom}:</span>
                        <span className="fw-600">{filteredHotels.length} </span> properties found
                      </span>
                    </div>
                  </div>
                </div>
              )}
              {pageLoading ? (
                <div className="hotels-list">
                  <div className='text-center'>
                    <img src="/assets/images/loader.webp" width={"64px"} height={"64px"} alt="Loading..." />
                    Loading, Please wait...
                  </div>
                </div>
              ) : (
                <div className="hotels-list">
                  {renderHotelCards()}
                  {loadingMore && <div className="text-center my-3"><img src="/assets/images/loader.webp" width={"32px"} height={"32px"} alt="Loading more..." /></div>}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HotelsList;